import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageShell from './PageShell';
import DialogFooter from './components/DialogFooter';
import Pagination from './components/Pagination';

import { showToastError } from './components/ToastNotifications';
import ProductSelectionModal from './components/ProductSelectionModal';
import type { ProductSelectionApi } from './components/ProductSelectionModal';
import LinkPackageSelector from './components/LinkPackageSelector';
import { getAvailableTags, isStageableTag, type StageableTagRecord, type UseStageTagsResult } from './hooks/usePackages';
import type { AvailableTagRecord } from './types';

type StageTagsDialogProps = {
    marketId?: number | string;
    facilityId?: number | string;
    marketName?: string;
    facilityName?: string;
    api?: (ProductSelectionApi & { getAvailableTags?: typeof getAvailableTags }) | null;
    organizationId?: number;
    tags?: AvailableTagRecord[];
    onBack: () => void;
    onClose: () => void;
    staging: UseStageTagsResult;
    onNext?: () => void;
};

export default function StageTagsDialog({
    marketId,
    facilityId,
    marketName,
    facilityName,
    api = null,
    organizationId,
    tags = [],
    onBack,
    onClose,
    staging,
    onNext: _onNext
}: StageTagsDialogProps) {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [productModalTag, setProductModalTag] = useState(null);
    const requestIdRef = useRef(0);
    const effectiveSearchRef = useRef('');

    const [state, setState] = useState({
        loading: false,
        loadError: null as string | null,
        data: tags,
        page: 0,
        pagePending: 0,
        pageSize: 12,
        total: 0,
        totalNoFilters: 0,
        allSelected: false
    });

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const nextSearch = search.trim().length >= 3 ? search.trim() : '';
            if (nextSearch === effectiveSearchRef.current) return;
            effectiveSearchRef.current = nextSearch;
            requestIdRef.current += 1;
            setState((prev) => ({ ...prev, page: 0, pagePending: 0 }));
            setDebouncedSearch(nextSearch);
        }, 300);
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const requestId = ++requestIdRef.current;

        async function getTags() {
            try {
                const searchTerm = debouncedSearch?.length > 0 ? debouncedSearch : null;
                const requestedPage = state.pagePending;
                const requestedPageSize = state.pageSize;
                setState(prev => ({ ...prev, loading: true }));
                const available = await (api?.getAvailableTags || getAvailableTags)({
                    organizationId: organizationId,
                    facilityId: facilityId,
                    tagLabel: searchTerm,
                    page: requestedPage,
                    pageSize: requestedPageSize,
                });
                if (requestId !== requestIdRef.current) return;
                if (available && 'error' in available && available.error) {
                    throw available;
                }
                const documents = Array.isArray(available?.documents) ? available.documents : [];
                const rawTotal = available?.total;
                const parsedTotal = Number(rawTotal);
                const total = rawTotal !== undefined && rawTotal !== null && String(rawTotal) !== '' && Number.isFinite(parsedTotal)
                    ? parsedTotal
                    : documents.length;
                setState(prev => ({
                    ...prev,
                    loading: false,
                    data: documents,
                    total,
                    page: requestedPage,
                    pagePending: requestedPage,
                }));
                if (!searchTerm?.length || !searchTerm?.trim()?.length) {
                    setState(prev => ({ ...prev, totalNoFilters: total }));
                }
            } catch {
                if (requestId !== requestIdRef.current) return;
                showToastError("Could not load tags");
                setState(prev => ({ ...prev, loading: false }));
            }
        }
        if (organizationId) {
            getTags();
        }
    }, [api, facilityId, organizationId, debouncedSearch, state.pagePending, state.pageSize]);

    const selected = useMemo(() => {
        return Array.isArray(state.data) ? state.data.filter(p => p.selected) : [];
    }, [state.data]);

    const toggle = (id) => {
        setState(prev => ({
            ...prev,
            data: prev.data.map(p =>
                p.id === id
                    ? { ...p, selected: !p.selected }
                    : p
            )
        }));
    };

    const setAll = () => {
        setState({
            ...state,
            allSelected: !state.allSelected,
            data: state.data.map(p => {
                const isInFilteredRecords = state.data.some(f => f.id === p.id);
                if (isInFilteredRecords) {
                    return { ...p, selected: !state.allSelected };
                } else {
                    return p;
                }
            })
        });
    };

    const assignProduct = (tag, product) => {
        const dataCopy = [...state.data]
        const index = dataCopy.findIndex(p => p.id === tag.id);
        dataCopy[index]['productId'] = product.id;
        dataCopy[index]['productName'] = product.name;
        setState({ ...state, data: dataCopy });
    };

    const unAssignProduct = (tag) => {
        const dataCopy = [...state.data]
        const index = dataCopy.findIndex(p => p.id === tag.id);
        dataCopy[index]['productId'] = null;
        dataCopy[index]['productName'] = null;
        setState({ ...state, data: dataCopy });
    }

    const assignPackageSearchText = (tag, text) => {
        const dataCopy = [...state.data]
        const index = dataCopy.findIndex(p => p.id === tag.id);
        dataCopy[index]['packageSearchText'] = text;
        setState({ ...state, data: dataCopy });
    };

    const assignPackage = (tag, pack) => {
        const dataCopy = [...state.data]
        const index = dataCopy.findIndex(p => p.id === tag.id);
        dataCopy[index]['packageId'] = pack.id;
        dataCopy[index]['packageLabel'] = pack.label;
        dataCopy[index]['packageSearchText'] = pack.label;
        setState({ ...state, data: dataCopy });
    };

    const unAssignPackage = (tag) => {
        const dataCopy = [...state.data]
        const index = dataCopy.findIndex(p => p.id === tag.id);
        dataCopy[index]['packageId'] = null;
        dataCopy[index]['packageLabel'] = null;
        dataCopy[index]['packageSearchText'] = null;
        setState({ ...state, data: dataCopy });
    };

    const handleNext = async () => {
        if (!selected.length || staging.isPending) return;
        const stageable: StageableTagRecord[] = [];
        for (const tag of selected) {
            // if package is not selected, search text must be empty too to avoid user confusion
            if (!tag.packageId && tag.packageSearchText) {
                showToastError(`Package is not selected but search text is provided - ${tag.label}`);
                return;
            }

            // if package is selected, product must be selected too
            if (!tag.productId) {
                showToastError(`Product is required - ${tag.label}`);
                return;
            }
            if (!isStageableTag(tag)) {
                showToastError(`Tag is missing an identifier - ${tag.label}`);
                return;
            }
            stageable.push(tag);
        }

        const result = await staging.submit(stageable);
        if (!result) return;
        const succeededIds = new Set(result.stagedTagIds);

        setState(prev => ({
            ...prev,
            allSelected: false,
            data: prev.data.map((tag) => succeededIds.has(tag.id)
                ? { ...tag, selected: false }
                : tag),
        }));
    };

    const footer = (
        <DialogFooter withTopDivider dividerStyle={{ marginTop: 12, marginBottom: 10 }}>
            <button className="button secondary" onClick={onBack} disabled={staging.isPending} data-testid="stage-tags-back">Back</button>
            <button
                className="button"
                onClick={handleNext}
                disabled={!selected.length || staging.isPending}
                aria-busy={staging.isPending}
                data-testid="stage-tags-next"
            >
                {staging.isPending ? 'Adding' : 'Next'}
            </button>
        </DialogFooter>
    );

    return (
        <PageShell
            contextLabel="Tags"
            title="Select Tags"
            onClose={onClose}
            testId="stage-tags"
            closeDisabled={staging.isPending}
            framed={false}
            preview={(
                <div className="card" style={{ padding: 16 }}>
                    <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Preview</div>
                    <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Tags to Import</div>
                    <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 6, marginBottom: 10 }}>
                        <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Market:</span> {marketName || '—'}</div>
                        <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Facility:</span> {facilityName || '—'}</div>
                        <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Total:</span> {state.totalNoFilters}</div>
                        <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Filtered:</span> {state.total}</div>
                        <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Selected:</span> {selected.length}</div>
                    </div>
                    <div className="divider" style={{ margin: '12px 0' }} />
                    <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Selected Tags</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                        {selected.length === 0 ? (
                            <div className="muted" style={{ fontSize: 12 }}>None selected yet.</div>
                        ) : (
                            Array.from(selected)
                                .slice(0, 8)
                                .map((s) => (
                                    <div key={s.id} className="card" style={{
                                        padding: 10,
                                        borderColor: state.data.find((p) => p.id === s.id && s.productId && s.packageId) ? 'green' : 'transparent'
                                    }}>
                                        <div style={{ fontWeight: 800, fontSize: 13 }}>{`${s.label}`}</div>
                                        {/*<div className="muted" style={{ fontSize: 12 }}>*/}

                                        {/*</div>*/}
                                    </div>
                                ))
                        )}
                        {selected.length > 8 && (
                            <div className="muted" style={{ fontSize: 12 }}>+{selected.length - 8} more</div>
                        )}
                    </div>
                </div>
            )}
            footer={footer}
        >
            <div className="form-grid">
                <div className="field full-span">
                    <label className="label">Filter</label>
                    <input
                        className="input"
                        placeholder="Search by ID, minimum 3 characters"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="stage-tags-search"
                    />
                </div>
                <div className="field full-span">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div>
                            <label className="label" style={{ marginBottom: 0 }}>Tags</label>
                            <div className="muted" style={{ fontSize: 12 }}>Showing up to {state.pageSize} at a time</div>
                        </div>
                    </div>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 8, minHeight: 260, maxHeight: 260, overflowY: 'auto' }}>
                        {state.data.length === 0 && <div className="muted" style={{ padding: 8 }}>No packages match this filter.</div>}
                        {state.total > 0 && (
                            <div >
                                <input
                                    type="checkbox"
                                    checked={selected.length === state.data.length}
                                    onChange={setAll}
                                    aria-label="Select all products"
                                    data-testid="stage-tags-select-all"
                                />
                                <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>Select all</span>

                            </div>
                        )}
                        {state.data.map((p) => {
                            return (
                                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px' }}>
                                    <input
                                        type="checkbox"
                                        checked={selected.some(s => s.id == p.id)}
                                        onChange={() => toggle(p.id)}
                                    />
                                    <div style={{ display: 'grid', gridTemplateColumns: "minmax(200px,1fr) 1fr 0.05fr 1fr", lineHeight: "40px" }}>

                                        <div style={{ fontWeight: 700 }}>{`${p.label}`}</div>
                                        <div style={{ fontWeight: 700, marginLeft: '1em' }}>
                                            {selected.some(s => s.id == p.id) && <LinkPackageSelector
                                                organizationId={organizationId}
                                                facilityId={facilityId}
                                                onSelect={(item) => assignPackage(p, item)}
                                                mappedValue={p.packageLabel || undefined}
                                                onClear={() => unAssignPackage(p)}
                                                onSearchTextChange={(text) => assignPackageSearchText(p, text)}
                                            />}
                                        </div>
                                        <div></div>
                                        <div>
                                            {selected.some(s => s.id == p.id) && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                                                        {p.productName || <span className="muted">No product</span>}
                                                    </span>
                                                    <button
                                                        className="button secondary"
                                                        style={{ fontSize: 12, padding: '4px 10px' }}
                                                        onClick={() => setProductModalTag(p)}
                                                        data-testid="select-product-btn"
                                                    >
                                                        {p.productId ? 'Change' : 'Select Product'}
                                                    </button>
                                                    {p.productId && (
                                                        <button
                                                            className="button secondary"
                                                            style={{ fontSize: 12, padding: '4px 10px' }}
                                                            onClick={() => unAssignProduct(p)}
                                                        >
                                                            Clear
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <Pagination
                        page={state.page}
                        totalPages={Math.ceil(state.total / state.pageSize)}
                        onPrev={() => { setState((prev) => ({ ...prev, pagePending: prev.page - 1 })) }}
                        onNext={() => { setState((prev) => ({ ...prev, pagePending: prev.page + 1 })) }}
                        prevDisabled={state.loading || staging.isPending}
                        nextDisabled={state.loading || staging.isPending}
                        prevLabel="◀"
                        nextLabel="▶"
                        format="of"
                    />
                </div>
            </div>
            <ProductSelectionModal
                show={!!productModalTag}
                api={api}
                organizationId={organizationId}
                initialMarketId={marketId}
                onCancel={() => setProductModalTag(null)}
                onSelect={(product) => {
                    if (productModalTag) assignProduct(productModalTag, product);
                    setProductModalTag(null);
                }}
            />
        </PageShell>
    );
}
